import { diInject } from '@di/di';
import { cloneDeep } from '@studio/utils/clone';
import { FeedError, handleError } from '@studio/utils/errors';
import { EventEmitter } from '@studio/utils/event-emitter';
import { FontLoader } from '@studio/utils/font-loading';
import { isSafeUrl, sanitizeUrl } from '@studio/utils/sanitizer';
import { concatUrl } from '@studio/utils/url';
import { decodeXML, mod } from '@studio/utils/utils';
import { FONT_SERVICE_TEXT_PARAMETER, getFontServiceParamsFromString } from '../../font-service.utils';
import { getElementTextProperties, isTextNode } from '../../nodes/helpers';
import { isContentSpan } from '../rich-text/utils';
import { decodeFeedPath, normalizeFeedData } from './feeds.utils';
const MANUAL_FEED_VALUE = 788400000;
export class FeedStore extends EventEmitter {
    constructor(brandId) {
        super();
        // Elements in the creative using a feeded value
        this.elements = new Map();
        this.feeds = new Map();
        this.loop = 1;
        this.fontCharacterCache = new Map();
        this._skipNextUpdate = false;
        this._destroyed = false;
        this._creativeConfig = diInject(2 /* Token.CREATIVE_CONFIG */);
        const feedStoreOrigin = this._creativeConfig.origins.FEEDS_STORAGE;
        this._URI = feedStoreOrigin ? concatUrl(feedStoreOrigin, brandId) : '';
    }
    preloadFeeds(feedIds) {
        const promises = [];
        for (const id of [...new Set(feedIds)]) {
            promises.push(this.add(id));
        }
        return Promise.all(promises);
    }
    resetIndexState() {
        this.elements.forEach(element => {
            element.currentIndex = element.feed.step.start - 1;
        });
        this.loop = 1;
    }
    updateElementsCurrentIndex() {
        if (this._skipNextUpdate) {
            this._skipNextUpdate = false;
            return;
        }
        this.elements.forEach(element => {
            const maxFeedLoops = element.items;
            element.currentIndex = this._calculateCurrentIndex(element.feed, maxFeedLoops);
        });
    }
    _calculateCurrentIndex(feed, maxFeedLoops) {
        const { size, start, occurrence } = feed.step;
        if (occurrence === 'loop') {
            /**
             * Make sure that the index never exceeds the maximum amount of items
             */
            return mod(start - 1 + (this.loop - 1) * size, maxFeedLoops);
        }
        else {
            return Math.min(feed.step.start - 1, maxFeedLoops - 1);
        }
    }
    skipNextIndexUpdate(skip = true) {
        this._skipNextUpdate = skip;
    }
    setFeedLoop(loop, exceedLimit) {
        if (exceedLimit) {
            this.loop = loop;
        }
        else if (this.loop < 0 && loop > 0) {
            this.loop += loop;
        }
        else {
            this.loop = Math.max(1, loop);
        }
    }
    async add(feedId, refetch) {
        var _a;
        if (!feedId) {
            return Promise.resolve({ data: [], intervalInSeconds: MANUAL_FEED_VALUE });
        }
        const feedEntry = this.feeds.get(feedId);
        if ((feedEntry === null || feedEntry === void 0 ? void 0 : feedEntry.feed) && !refetch) {
            return feedEntry.feed;
        }
        this.feeds.set(feedId, {});
        const response = await this.fetchFeedData(feedId);
        this.feeds.set(feedId, { feed: response });
        if ((_a = feedEntry === null || feedEntry === void 0 ? void 0 : feedEntry.interval) === null || _a === void 0 ? void 0 : _a.id) {
            clearInterval(feedEntry.interval.id);
        }
        const isManualFeedInterval = response.intervalInSeconds >= MANUAL_FEED_VALUE;
        const intervalInSeconds = response.intervalInSeconds * 1000;
        const updateInterval = {
            frequency: intervalInSeconds,
            id: !this._destroyed && !isManualFeedInterval
                ? setInterval(() => this.updateFeedData(feedId), intervalInSeconds)
                : undefined
        };
        this.feeds.set(feedId, {
            feed: response,
            interval: updateInterval
        });
        return response;
    }
    async fetchFeedData(id, tryGetCachedData) {
        var _a;
        const feed = this.feeds.get(id);
        if (feed && (feed.overridden || tryGetCachedData)) {
            return feed.feed;
        }
        const frequencyMs = ((_a = feed === null || feed === void 0 ? void 0 : feed.interval) === null || _a === void 0 ? void 0 : _a.frequency) || 60000;
        const s = Math.floor(Date.now() / 1000);
        const interval = Math.min(frequencyMs / 1000, 60);
        const cb = s - (s % interval);
        /**
         * Deterministic cache buster to make all request within the frequency
         * call the same url so the CDN can protect the service
         */
        const cbString = this._creativeConfig.environment === 1 /* AdEnvironment.App */ ? `?cb=${cb}` : '';
        const url = concatUrl(this._URI, `${id}.json${cbString}`);
        try {
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            });
            if (!response.ok) {
                throw Error(response.statusText);
            }
            const responseData = (await response.json());
            const normalizedFeedData = normalizeFeedData(responseData);
            return normalizedFeedData;
        }
        catch (e) {
            throw new FeedError(`Could not fetch feed data: ${e}`);
        }
    }
    async updateFeedData(id) {
        if (this._destroyed && this.feeds.size === 0) {
            return;
        }
        const response = await this.fetchFeedData(id);
        const feedEntry = this.feeds.get(id);
        if (!feedEntry) {
            handleError(`Feed not found in feed store`, {
                contexts: {
                    error: new FeedError('Feed not found in feed store'),
                    feeds: Object.fromEntries(this.feeds),
                    feedId: id
                }
            });
            return;
        }
        feedEntry.feed = response;
        this.loadFontsBasedOnFeedData(id);
        this.emit('dataChanged', { id, feedData: feedEntry.feed });
    }
    loadFontsBasedOnFeedData(id, skipFontInjection = false) {
        var _a, _b, _c, _d, _e, _f, _g;
        // Don't load fonts in Studio, since we already have all characters.
        if (this._creativeConfig.environment === 1 /* AdEnvironment.App */) {
            return;
        }
        const response = this.feeds.get(id).feed;
        const fonts = new Map();
        const tryTransformToUpperCase = (str, span, isUppercase) => {
            if (span.style.uppercase || isUppercase) {
                return str.toLocaleUpperCase();
            }
            return str;
        };
        const feedDataValuesMap = response.data.reduce((map, item) => {
            var _a, _b;
            for (const key in item) {
                const mappedItem = map.get(key);
                const value = (_b = (_a = item[key].value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '';
                if (mappedItem) {
                    mappedItem.push(value);
                }
                else {
                    map.set(key, [value]);
                }
            }
            return map;
        }, new Map());
        const elements = Array.from(this.elements.values());
        for (const element of elements) {
            if (!isTextNode(element.element)) {
                continue;
            }
            const textElement = element.element;
            let textContent = '';
            let inlineStyledTextContent = '';
            const isUppercase = !!textElement.uppercase;
            for (const span of textElement.content.spans) {
                if (!isContentSpan(span)) {
                    continue;
                }
                const isMatchingVariableSpan = span.type === "variable" /* SpanType.Variable */ &&
                    span.style.variable &&
                    span.style.variable.id === id;
                if (isMatchingVariableSpan) {
                    const feedValues = (_b = (_a = feedDataValuesMap.get(decodeFeedPath(span.style.variable.path))) === null || _a === void 0 ? void 0 : _a.join('')) !== null && _b !== void 0 ? _b : '';
                    if (span.style.font) {
                        inlineStyledTextContent += tryTransformToUpperCase(feedValues, span, isUppercase);
                    }
                    else {
                        textContent += tryTransformToUpperCase(feedValues, span, isUppercase);
                    }
                }
                else if ((_c = span.style) === null || _c === void 0 ? void 0 : _c.font) {
                    inlineStyledTextContent += tryTransformToUpperCase(span.content, span, isUppercase);
                }
                else {
                    textContent += tryTransformToUpperCase(span.content, span, isUppercase);
                }
                if ((_d = span.style) === null || _d === void 0 ? void 0 : _d.font) {
                    const f = fonts.get(span.style.font.id);
                    const content = ((_e = f === null || f === void 0 ? void 0 : f.content) !== null && _e !== void 0 ? _e : '') + inlineStyledTextContent;
                    fonts.set(span.style.font.id, {
                        content,
                        font: Object.assign({}, span.style.font)
                    });
                }
            }
            if (textElement.font) {
                const isUpperCase = getElementTextProperties(textElement).uppercase;
                const content = (_g = (_f = fonts.get(textElement.font.id)) === null || _f === void 0 ? void 0 : _f.content) !== null && _g !== void 0 ? _g : '';
                fonts.set(textElement.font.id, {
                    content: content + (isUpperCase ? textContent.toLocaleUpperCase() : textContent),
                    font: Object.assign({}, textElement.font)
                });
            }
        }
        for (const [fontId, { content, font }] of fonts.entries()) {
            if (font.src.startsWith('data')) {
                FontLoader.injectFontFace(font);
                continue;
            }
            const cachedFont = this.fontCharacterCache.get(fontId);
            let hasDiff = false;
            if (cachedFont) {
                const characters = Array.from(content);
                const cachedFontSet = new Set(Array.from(cachedFont));
                hasDiff = characters.some(char => !cachedFontSet.has(char));
            }
            if (hasDiff || !cachedFont) {
                const characters = content + (cachedFont || '');
                const { textParams, distinctText } = getFontServiceParamsFromString(characters);
                this.fontCharacterCache.set(fontId, distinctText);
                const url = font.src.split(FONT_SERVICE_TEXT_PARAMETER)[0];
                font.src = url + textParams;
                if (!skipFontInjection) {
                    FontLoader.loadFontFace(font);
                }
            }
        }
    }
    getFeedValue(feed, elementId, element) {
        var _a, _b;
        const field = decodeURIComponent(feed.path);
        try {
            // Set/Store the feeded element
            this.addFeedElement(elementId, feed, element);
            const feededElement = this.elements.get(elementId);
            if (!feededElement) {
                throw new FeedError('Could not get feeded element');
            }
            const feedItem = (_b = (_a = this.feeds.get(feededElement.feed.id)) === null || _a === void 0 ? void 0 : _a.feed) === null || _b === void 0 ? void 0 : _b.data[feededElement.currentIndex][field];
            if (!feedItem) {
                throw new FeedError(`Could not find any feed items with the field ${field}`);
            }
            // Make sure to never set any js or other potential treat as targeturl
            const targetUrl = typeof feedItem.targetUrl === 'string' ? sanitizeUrl(feedItem.targetUrl) : undefined;
            const value = typeof feedItem.value === 'string' ? decodeXML(feedItem.value) : feedItem.value;
            return {
                value,
                targetUrl
            };
        }
        catch (e) {
            console.warn(e);
            if (this.elements.get(elementId)) {
                return { value: this.elements.get(elementId).feed.fallback, targetUrl: undefined };
            }
            else {
                return { value: '', targetUrl: undefined };
            }
        }
    }
    getFeedValueUrl(feed, elementId, element) {
        if (!feed) {
            return;
        }
        const feedValue = this.getFeedValue(feed, elementId, element);
        if (!feedValue.value) {
            return;
        }
        if (typeof feedValue.value === 'string' && isSafeUrl(feedValue.value)) {
            return sanitizeUrl(feedValue.value);
        }
    }
    addFeedElement(elementId, feed, element) {
        try {
            const maxFeedLoops = this.feeds.get(feed.id).feed.data.length;
            const existingElement = this.elements.get(elementId);
            if (existingElement) {
                this.elements.delete(elementId);
            }
            this.elements.set(elementId, {
                items: maxFeedLoops,
                currentIndex: this._calculateCurrentIndex(feed, maxFeedLoops),
                feed: cloneDeep(feed),
                element
            });
        }
        catch (_a) {
            /* empty */
        }
    }
    patchElementReference_m(element, feedElementId, feed) {
        this.addFeedElement(feedElementId, feed, element);
    }
    getFeed(id) {
        const feedData = this.feeds.get(id);
        if (feedData) {
            return feedData.feed;
        }
        return undefined;
    }
    destroy() {
        this._destroyed = true;
        this.feeds.forEach(feed => {
            if (feed.interval) {
                clearInterval(feed.interval.id);
            }
        });
        this.feeds.clear();
        this.elements.clear();
        this.clearEvents();
    }
}
